import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentRounded";
import CasesIcon from "@mui/icons-material/CasesRounded";
import InboxIcon from "@mui/icons-material/InboxRounded";
import Logout from "@mui/icons-material/LogoutRounded";
import NewspaperIcon from "@mui/icons-material/NewspaperRounded";
import { Box, Divider, Drawer, IconButton, List, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { Link, useMatch } from "react-router";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import useClientContext from "../../../contexts/useClientContext";
import { pageRoutes } from "../../../routes";
import { impersonationSelector } from "../../../store/state/user/selectors";
import UserComponent from "../user/UserComponent";
import HelpSupportRow from "./HelpSupportRow";
import { MainMenuItem } from "./MainMenuItem";
import PortalSwitcherRow from "./PortalSwitcherRow";

interface Props {
  anchorEl: null | HTMLElement;
  onClose: () => void;
}

export const MainMenu = ({ anchorEl, onClose }: Props) => {
  const {
    clientCode,
    portalSettings,
    portfolioWidget,
    isNewsPageAvailable,
    isDashboardPageAvailable,
    isFeatureEnabled,
  } = useClientContext();

  const isImpersonation = useSelector(impersonationSelector);

  const routeMatch = useMatch("/:client/:pageName/*");
  const pageName = routeMatch?.params["pageName"];

  return (
    <Drawer sx={{ ".MuiPaper-root": { width: "66.6%" } }} anchor="left" open={Boolean(anchorEl)} onClose={onClose}>
      <Box sx={{ display: "flex", width: "100%", flexDirection: "column", flex: 1, py: 2 }}>
        <Box sx={{ px: 2 }}>
          <PortalSwitcherRow onNavigate={onClose} />
        </Box>

        <Divider sx={{ py: 1 }} />

        <List>
          {isFeatureEnabled("Inbox") && (
            <MainMenuItem
              text={portalSettings?.inboxPageName || "Inbox"}
              page={`${pageRoutes.inbox}/Inbox/p0`}
              isActive={pageName === pageRoutes.inbox}
              Icon={InboxIcon}
              onClick={onClose}
            />
          )}
          {isFeatureEnabled("Fundraising") && (
            <MainMenuItem
              text="Fundraising"
              page={pageRoutes.fundraising}
              Icon={ArticleIcon}
              onClick={onClose}
              isActive={pageName === pageRoutes.fundraising}
            />
          )}
          {isDashboardPageAvailable && (
            <MainMenuItem
              text="Dashboard"
              page={pageRoutes.dashboard}
              Icon={AssessmentIcon}
              onClick={onClose}
              isActive={pageName === pageRoutes.dashboard}
            />
          )}
          {isNewsPageAvailable && (
            <MainMenuItem
              text="News"
              page={pageRoutes.news}
              Icon={NewspaperIcon}
              onClick={onClose}
              isActive={pageName === pageRoutes.news}
            />
          )}
          {isFeatureEnabled("PortfolioCompanies") && portfolioWidget !== undefined && (
            <MainMenuItem
              text="Portfolio"
              page={pageRoutes.portfolio}
              Icon={CasesIcon}
              onClick={onClose}
              isActive={pageName === pageRoutes.portfolio}
            />
          )}
        </List>

        <HorizontalFill />

        <List>
          <HelpSupportRow />
        </List>

        <Divider />

        <Stack direction="row" sx={{ gap: 2, px: 2, pt: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <UserComponent />
          </Box>
          <IconButton
            component={Link}
            to={`/${clientCode}/logout`}
            disabled={isImpersonation}
            onClick={onClose}
            sx={{ flexShrink: 0 }}
          >
            <Logout />
          </IconButton>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default MainMenu;
